import React from 'react'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import { ButtonSize, ButtonType } from '@app/types'


interface ButtonProps {
  id?: string;
  className?: string
  size?: ButtonSize
  to?: string
  href?: string
  fluid?: boolean
  [key: `fluid${number}`]: boolean | string | undefined;
  type?: ButtonType
  disabled?: boolean;
  inactive?: boolean;
  children?: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>) => void;
  textColor?: string;
  border?: string;
  borderRadius?: string;
  padding?: string;
  height?: string;
}

const Button: React.FC<ButtonProps> = props => {
  const activeFluidClass = Object.keys(props).find(
    (key) => key.startsWith('fluid') && key !== 'fluid' && props[key] === true
  ) || ''

  const classes = [
    'Button',
    'size-' + (props.size ? props.size : 'normal'),
    'type-' + (props.type ? props.type : 'normal'),
    props.className,
    props.fluid ? 'fluid' : '',
    activeFluidClass,
    props.disabled ? 'disabled' : '',
    props.inactive ? 'inactive' : '',
  ].join(' ')

  return <ButtonStyled
    id={props.id}
    as={props.href ? 'a' : props.to ? Link : 'button'}
    to={props.disabled ? '' : props.to}
    href={props.href}
    onClick={props.onClick}
    className={classes}
    disabled={props.disabled}
    color={props.textColor}
    border={props.border}
    borderRadius={props.borderRadius}
    padding={props.padding}
    height={props.height}
  >
    {props.children}
  </ButtonStyled>
}

const generateFluidClasses = () => {
  let styles = ''
  for (let i = 1; i <= 100; i++) {
    styles += `
      &.fluid${i} {
        width: ${i}%;
      }
    `
  }
  return styles
}

const ButtonStyled = styled.button.attrs<{type?: string}>(props => ({
  type: props.type || 'button'
}))<{color: string, border?: string, borderRadius: string, padding?: string, height?: string}>`

  border: ${({ border }) => border ? border : 'none'};
  cursor: pointer;
  border-radius: ${({ borderRadius }) => borderRadius ? borderRadius : '5px'};
  color:${({ color }) => color ? color : 'var(--font-color-primary)'} ;
  background-color: var(--bg-undecimary);
  text-decoration: none;
  padding: ${({ padding }) => padding ? `${padding} !important` : 'none'};
  font-size: var(--font-size-normal);
  ${({ height }) => height && `height: ${height};`}

  &.size-xsmall {
    padding: 0.2rem;
    width: 2rem;
  }
  &.size-small {
  }
  &.size-normal {
    padding: 0.5rem;
  }
  &.size-normal-nopadding {
    padding: 0;
  }
  &.size-normal-left-margin {
    margin-left: 0.5rem;
    padding: 0;

  }
  &.size-mid-normal {
    padding: 0.5rem;
  }
  &.size-big {
    font-size: var(--font-size-big);
    padding: 1rem 0.5rem;
    font-weight: bold;
  }

  &.type-primary {
    background-color: var(--bg-quinary);
    color: #fff;
  }

  &.type-primary-dark-refreshed {
    background-color: var(--color-brand-dark-new);
  }
    &.type-primary-gray-light {
    background-color: var(--new-color-gray-light);
  }
  &.type-button-color {
    background-color: var(--r-color-button);
    color: #fff;
  }
  &.type-button-success {
    background-color: var(--r-color-logo-green);
  }

  &.type-primary-dark {
    background-color: var(--color-brand-dark);
  }

  &.type-primary-pale {
    background-color: var(--color-brand-pale);
  }

  &.type-dark {
    background-color: #4D4D4D;
  }

  &.type-white {
    background-color: white;
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.1), 0 -1px 6px rgba(0, 0, 0, 0.1);
  }

  &.type-black {
    background-color: black;
    color: white;
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.1), 0 -1px 6px rgba(0, 0, 0, 0.1);
  }

  &.type-transparent {
    background-color: transparent;
  }

  &.type-gray-C {
    background-color: var(--r-color-gray-C);
  }

  &.type-warning {
    background-color: var(--color-warning);
    color: var(--font-color-inverted);
  }
  &.type-primary-warning {
    background-color: var( --color-brand);
    color: var(--color-warning);
  }

  &.noshadow {
    box-shadow: none;
  }

  &.fluid {
    width: 100%;
  }

  ${generateFluidClasses()}

  &.disabled {
    opacity: .3;
  }

  &.inactive {
    background-color: var(--bg-tertiary);
  }

  &.channelList {
      width: 20%;
      color: white
  }
`


export default Button
